import type { GeneratedRecipe, GeneratedRecipeStep, KitchenPreferences, KitchenToolRequest, NutritionEstimate, ShoppingItem } from "@passio/api-client";

export type MediaKind = "image" | "video" | "audio";

/** The media for a kitchen tool, already base64 data URIs. `bytes` is the total. */
export interface MediaValue {
  kind: MediaKind;
  dataUri: string;
  bytes: number;
  name?: string;
  /** Photos only: every photo in order (the first is `dataUri`), sent as `images` when there are several. */
  photos?: string[];
}

/** The API takes 25 MB of media per call; the demo proxy's 36 MB body limit fits that as base64. */
export const MAX_MEDIA_BYTES = 25 * 1024 * 1024;
export const MAX_PHOTOS = 4;

export const fmtMB = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export function mediaSizeError(bytes: number): string | null {
  return bytes > MAX_MEDIA_BYTES ? `That file is ${fmtMB(bytes)}; keep it under ${fmtMB(MAX_MEDIA_BYTES)}, the API's limit per call.` : null;
}

/** Decoded size of a base64 data URI. */
export const dataUriBytes = (uri: string) => Math.round((uri.length - uri.indexOf(",") - 1) * 0.75);

/** Photos as one media value; null when there are none. */
export function photosMedia(photos: string[]): MediaValue | null {
  if (!photos.length) return null;
  return { kind: "image", dataUri: photos[0]!, bytes: photos.reduce((n, p) => n + dataUriBytes(p), 0), photos };
}

/** Drop MIME parameters (`;codecs=vp8,opus`) that recorders add, so the type reads cleanly upstream. */
export function plainMimeType(type: string, fallback: string): string {
  const base = type.split(";")[0]?.trim().toLowerCase();
  return base || fallback;
}

/** "eggs, spinach\nfeta" to ["eggs", "spinach", "feta"]: trimmed, empties and repeats dropped. */
export function splitList(s: string | undefined | null): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of (s ?? "").split(/[,;\n]/)) {
    const v = part.trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      out.push(v);
    }
  }
  return out;
}

export interface PreferencesForm {
  likes?: string;
  dislikes?: string;
  diet?: string[];
  allergies?: string[];
  conditions?: string[];
  goals?: string[];
  servings?: number | string;
  maxMinutes?: number | string;
  cuisine?: string;
  skill?: string;
  equipment?: string;
  timeOfDay?: string;
  targets?: KitchenPreferences["targets"];
}

const positive = (v: number | string | undefined) => {
  const n = typeof v === "string" ? Number(v) : v;
  return n && Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
};

/** Form fields to `preferences`, leaving out anything empty; undefined when nothing is set. */
export function buildPreferences(f: PreferencesForm): KitchenPreferences | undefined {
  const targets = Object.fromEntries(Object.entries(f.targets ?? {}).filter(([, v]) => typeof v === "number" && v > 0));
  const prefs: KitchenPreferences = {
    likes: splitList(f.likes),
    dislikes: splitList(f.dislikes),
    diet: f.diet ?? [],
    allergies: f.allergies ?? [],
    conditions: f.conditions ?? [],
    goals: f.goals ?? [],
    servings: positive(f.servings),
    maxMinutes: positive(f.maxMinutes),
    cuisine: f.cuisine?.trim(),
    skill: f.skill?.trim(),
    equipment: splitList(f.equipment),
    timeOfDay: f.timeOfDay?.trim(),
    targets: Object.keys(targets).length ? targets : undefined,
  };
  const kept = Object.fromEntries(Object.entries(prefs).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "")));
  return Object.keys(kept).length ? (kept as KitchenPreferences) : undefined;
}

/** Text plus the media (one item, or several photos), as the request body. Empty fields are left out. */
export function kitchenRequest({ text, media, message, preferences, ...extra }: { text?: string; media?: MediaValue | null; message?: string; preferences?: KitchenPreferences } & Partial<KitchenToolRequest>): KitchenToolRequest {
  const req: KitchenToolRequest = { ...extra };
  if (text?.trim()) req.text = text.trim();
  if (media?.kind === "image" && (media.photos?.length ?? 0) > 1) req.images = media.photos;
  else if (media) req[media.kind] = media.dataUri;
  if (message?.trim()) req.message = message.trim();
  if (preferences) req.preferences = preferences;
  for (const k of Object.keys(req) as (keyof KitchenToolRequest)[]) {
    const v = req[k];
    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) delete req[k];
  }
  return req;
}

/** A pasted day log: parsed JSON when it is JSON, else the text as is; undefined when blank. */
export function parseDayLog(s: string): unknown {
  const t = s.trim();
  if (!t) return undefined;
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

/**
 * The follow-up call for an alternate title: same text and preferences, `selectedMeal` set, and the
 * detected ingredient names instead of the media, so the photo or video isn't uploaded (or billed) again.
 */
export function alternateRequest(base: KitchenToolRequest, title: string, detected?: { name?: string }[]): KitchenToolRequest {
  const { image: _image, images: _images, video: _video, audio: _audio, ...rest } = base;
  const names = (detected ?? []).map((d) => d.name?.trim() ?? "").filter(Boolean);
  const photo = Boolean(_image || _images?.length);
  const sentMedia = Boolean(photo || _video || _audio);
  const req: KitchenToolRequest = { ...rest, selectedMeal: title };
  if (names.length) req.ingredients = names;
  // Without detected names (recreate a dish) the title is enough, but say what the original was.
  else if (sentMedia && !rest.text) req.text = `Something like the dish in my earlier ${photo ? (_images?.length ? "photos" : "photo") : _video ? "video" : "voice note"}.`;
  return req;
}

const sameName = (a: string, b: string) => {
  const x = a.trim().toLowerCase();
  const y = b.trim().toLowerCase();
  return x === y || x.includes(y) || y.includes(x);
};

/**
 * What to buy for a recipe, for the shopping page: the missing ingredients (with the recipe's amount when
 * it lists them), or every ingredient the user doesn't have when the tool didn't name any.
 */
export function recipeCartItems(recipe: GeneratedRecipe | null | undefined, missing: string[] = []): ShoppingItem[] {
  const ings = recipe?.ingredients ?? [];
  const names = missing.length ? missing : ings.filter((i) => i.onHand === false).map((i) => i.name);
  return names
    .filter((n) => n?.trim())
    .map((name) => {
      const ing = ings.find((i) => i.name && sameName(i.name, name));
      const qty = ing?.quantity ?? undefined;
      return qty && ing?.unit ? { name: name.trim(), portionQuantity: qty, portionSize: ing.unit } : { name: name.trim() };
    });
}

export function fmtMinutes(m: number | null | undefined): string {
  if (!m || m <= 0) return "";
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return h ? (min ? `${h} h ${min} min` : `${h} h`) : `${min} min`;
}

export function stepTemperature(s: Partial<Pick<GeneratedRecipeStep, "temperatureC" | "temperatureF">>): string {
  const c = s.temperatureC ?? (s.temperatureF ? ((s.temperatureF - 32) * 5) / 9 : undefined);
  const f = s.temperatureF ?? (s.temperatureC ? (s.temperatureC * 9) / 5 + 32 : undefined);
  return c && f ? `${Math.round(c)}°C / ${Math.round(f)}°F` : "";
}

export const HEAT_COLORS: Record<string, string> = {
  low: "yellow",
  "medium-low": "orange.4",
  medium: "orange",
  "medium-high": "red.5",
  high: "red.8",
};

/** True when an estimate has any number worth showing. */
export const hasNutrition = (n: NutritionEstimate | null | undefined): n is NutritionEstimate =>
  Boolean(n && Object.values(n).some((v) => typeof v === "number" && v > 0));

/** Average channels into one. */
export function downmix(channels: Float32Array[]): Float32Array {
  if (channels.length === 1) return channels[0]!;
  const out = new Float32Array(channels[0]?.length ?? 0);
  for (const ch of channels) for (let i = 0; i < out.length; i++) out[i]! += (ch[i] ?? 0) / channels.length;
  return out;
}

/** 16-bit PCM mono WAV bytes: a format every model reads, unlike browser-recorded WebM/Opus. */
export function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const ascii = (at: number, s: string) => [...s].forEach((ch, i) => v.setUint8(at + i, ch.charCodeAt(0)));
  ascii(0, "RIFF");
  v.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  ascii(36, "data");
  v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Uint8Array(buf);
}

export const WAV_SAMPLE_RATE = 16_000;

/** Decode any audio the browser can play and re-encode it as 16 kHz mono WAV. Browser only. */
export async function toWav16k(blob: Blob): Promise<Blob> {
  const raw = await blob.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(raw);
    const frames = Math.max(1, Math.ceil(decoded.duration * WAV_SAMPLE_RATE));
    const offline = new OfflineAudioContext(1, frames, WAV_SAMPLE_RATE);
    const src = offline.createBufferSource();
    src.buffer = decoded;
    src.connect(offline.destination);
    src.start();
    const rendered = await offline.startRendering();
    const channels = Array.from({ length: rendered.numberOfChannels }, (_, i) => rendered.getChannelData(i));
    return new Blob([encodeWav(downmix(channels), WAV_SAMPLE_RATE) as BlobPart], { type: "audio/wav" });
  } finally {
    void ctx.close();
  }
}
